/**
 * Stickman Jumping Grid — pure game state machine.
 *
 * Echoes the in-store iDancing arcade rule (格子遊戲): a random sequence
 * of tiles light up, the player must tap them back in order. Correct
 * sequence → stickman hops through the tiles, score +1.
 *
 * Pure fn only — no DOM, no DOM types, no timers. The React component
 * owns animation timing + input; this module owns rules + scoring.
 * That split keeps rules 100% unit-testable.
 *
 * Sizing heuristic: party ≤ 4 → 3×3, party ≥ 5 → 4×4. Matches the
 * real venue's "big zone gets used for bigger parties" pattern.
 */

export type Phase = 'idle' | 'show' | 'input' | 'success' | 'fail' | 'done';

export interface Tile {
  readonly row: number; // 0-indexed from top
  readonly col: number; // 0-indexed from left
}

export interface GameState {
  readonly phase: Phase;
  readonly gridSize: number; // 3 for 3×3, 4 for 4×4
  readonly roundsRequired: number;
  readonly roundIndex: number; // 0-indexed current round
  readonly targetSequence: readonly Tile[]; // current round's sequence
  readonly inputSequence: readonly Tile[]; // player's taps so far
  readonly score: number;
  /** Stickman position (bottom-left origin) — independent from tile taps. */
  readonly stickman: Tile;
}

export interface GameConfig {
  readonly partySize: number;
  /** Deterministic PRNG for tests; defaults to Math.random. */
  readonly rand?: () => number;
  /** Number of rounds before the game is `done`. */
  readonly rounds?: number;
}

export function gridSizeFor(partySize: number): 3 | 4 {
  return partySize >= 5 ? 4 : 3;
}

function pickRandomTile(gridSize: number, rand: () => number): Tile {
  return {
    row: Math.floor(rand() * gridSize),
    col: Math.floor(rand() * gridSize),
  };
}

function generateSequence(
  gridSize: number,
  length: number,
  rand: () => number,
): readonly Tile[] {
  const out: Tile[] = [];
  let last: Tile | null = null;
  for (let i = 0; i < length; i += 1) {
    // Avoid repeating the same tile back-to-back — visually confusing.
    let candidate = pickRandomTile(gridSize, rand);
    let tries = 0;
    while (last && candidate.row === last.row && candidate.col === last.col && tries < 4) {
      candidate = pickRandomTile(gridSize, rand);
      tries += 1;
    }
    out.push(candidate);
    last = candidate;
  }
  return out;
}

function tileEq(a: Tile, b: Tile): boolean {
  return a.row === b.row && a.col === b.col;
}

export function initGame(cfg: GameConfig): GameState {
  const gridSize = gridSizeFor(cfg.partySize);
  const rand = cfg.rand ?? Math.random;
  const roundsRequired = cfg.rounds ?? 3;
  const sequenceLength = 2; // round 1 = 2 taps; scales with roundIndex later if desired
  return {
    phase: 'show',
    gridSize,
    roundsRequired,
    roundIndex: 0,
    targetSequence: generateSequence(gridSize, sequenceLength, rand),
    inputSequence: [],
    score: 0,
    stickman: { row: gridSize - 1, col: 0 }, // bottom-left
  };
}

/** Advance past the "show" phase into player input. */
export function startInput(state: GameState): GameState {
  if (state.phase !== 'show') return state;
  return { ...state, phase: 'input', inputSequence: [] };
}

/**
 * Player taps a tile. Validates against current step in target sequence:
 *   - wrong tap → fail (score not credited; player can retry this round)
 *   - correct tap → append; if sequence complete → success + score +1
 *     + stickman hops to last tapped tile
 */
export function tapTile(state: GameState, tile: Tile): GameState {
  if (state.phase !== 'input') return state;
  const idx = state.inputSequence.length;
  const expected = state.targetSequence[idx];
  if (!expected || !tileEq(tile, expected)) {
    return { ...state, phase: 'fail' };
  }
  const nextInput = [...state.inputSequence, tile];
  if (nextInput.length < state.targetSequence.length) {
    return { ...state, inputSequence: nextInput };
  }
  // Sequence complete
  return {
    ...state,
    phase: 'success',
    inputSequence: nextInput,
    score: state.score + 1,
    stickman: tile, // hop to last tapped tile
  };
}

/** After success or fail, advance to next round (or `done`). */
export function nextRound(state: GameState, rand: () => number = Math.random): GameState {
  if (state.phase !== 'success' && state.phase !== 'fail') return state;
  const nextIdx = state.roundIndex + 1;
  if (nextIdx >= state.roundsRequired) {
    return { ...state, phase: 'done' };
  }
  return {
    ...state,
    phase: 'show',
    roundIndex: nextIdx,
    targetSequence: generateSequence(state.gridSize, 2, rand),
    inputSequence: [],
  };
}

/** Reset to start a fresh game with the same party size. */
export function resetGame(cfg: GameConfig): GameState {
  return initGame(cfg);
}
